import React, { memo, useCallback, useRef, useState } from 'react';
import { Card, CardContent, Box, Typography, FormControl, Select, MenuItem, Tooltip, IconButton, Collapse } from '@mui/material';
import { ExpandMore } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { reportStyles, reportMenuProps } from '../../../shared/globalSX';
import { COLORS } from '../../../shared/colors';
import { Skeleton } from '@mui/material';
import { WEEK_DAYS_SINGLE_LETTER, WEEK_DAYS_SHORT } from '../../../shared/appConstants';
import useCloseOnScroll from '../../../shared/useCloseOnScroll';

const CalendarChart = memo(({ selectedMonth, setSelectedMonth, selectedYear, setSelectedYear, calendarData, calendarData2, isMultiMonthRange, isTwoMonthRange, startDate, months, getTicketColor, handleDateClick, defaultExpanded = true, canCollapse = true, isLoading = false }) => {
  const { t } = useTranslation();
  const [isExpanded, setIsExpanded] = React.useState(defaultExpanded);
  const [activeDayTooltip, setActiveDayTooltip] = useState(null);
  const cardRef = useRef(null);

  const closeDayTooltip = useCallback(() => {
    setActiveDayTooltip(null);
  }, []);

  useCloseOnScroll(cardRef, useCallback(() => {
    closeDayTooltip();
  }, [closeDayTooltip]));

  const dayTooltipProps = {
    arrow: true,
    disableHoverListener: true,
    disableFocusListener: true,
    disableTouchListener: true,
    disableInteractive: true,
    placement: 'top',
    PopperProps: {
      disablePortal: true,
      modifiers: [
        {
          name: 'preventOverflow',
          options: {
            boundary: 'clippingParents',
            altBoundary: true,
            padding: 8,
          },
        },
        {
          name: 'flip',
          options: {
            fallbackPlacements: ['bottom', 'top-start', 'bottom-start'],
            padding: 8,
          },
        },
        {
          name: 'offset',
          options: {
            offset: [0, 8],
          },
        },
      ],
    },
    classes: { tooltip: 'app-tooltip' },
  };

  // If collapse is disabled, ensure it stays expanded
  const expanded = canCollapse ? isExpanded : true;

  return (
    <Card ref={cardRef} sx={{ minHeight: expanded ? 480 : 'auto', maxHeight: expanded ? 480 : 'auto', height: '100%', width: '100%', transition: 'all 0.3s', overflow: 'hidden' }} data-chart-type="calendar">
      <CardContent sx={{ pb: expanded ? 0 : '16px !important', '&:last-child': { pb: expanded ? 0 : '16px !important' } }}>
        <Box 
          display="flex" 
          justifyContent="space-between" 
          alignItems="center" 
          pb={1.5}
          mb={expanded ? 2 : 0}
          sx={{ borderBottom: '1px solid #082B72' }}
        >
          <Box 
            display="flex" 
            alignItems="center" 
            gap={1} 
            sx={{ cursor: canCollapse ? 'pointer' : 'default' }} 
            onClick={() => canCollapse && setIsExpanded(!isExpanded)}
          >
            {canCollapse && (
              <IconButton size="small" sx={{ color: 'text.secondary', p: 0 }}>
                {isExpanded ? <ExpandMore /> : <ExpandMore sx={{ transform: 'rotate(-90deg)' }} />}
              </IconButton>
            )}
            <Typography
              variant="h6"
              sx={{ fontWeight: 500, color: '#082B72' }}
            >
              {t('reports.charts.dailyTicketCreation')}
            </Typography>
          </Box>
          <Box display="flex" gap={1} alignItems="center">
            <Box display="flex" gap={1}>
              <FormControl size="small" sx={{ minWidth: 120 }}>
                <Select
                  value={selectedMonth}
                  onChange={(e) => setSelectedMonth(e.target.value)}
                  sx={{ 
                    height: 32, 
                    fontSize: '13px',
                    ...reportStyles.reportSelect
                  }}
                  MenuProps={reportMenuProps}
                >
                  {months.map((month, index) => (
                    <MenuItem key={index} value={index}>{month}</MenuItem>
                  ))}
                </Select>
              </FormControl>
              <FormControl size="small" sx={{ minWidth: 100 }}>
                <Select
                  value={selectedYear}
                  onChange={(e) => setSelectedYear(e.target.value)}
                  sx={{ 
                    height: 32, 
                    fontSize: '13px',
                    ...reportStyles.reportSelect
                  }}
                  MenuProps={reportMenuProps}
                >
                  {Array.from({ length: 10 }, (_, i) => new Date().getFullYear() - 5 + i).map(year => (
                    <MenuItem key={year} value={year}>{year}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>
          </Box>
        </Box>

        <Collapse in={expanded}>
          <Box sx={{ width: '100%', mt: 2 }}>
            {(isLoading || calendarData === null) ? (
               <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: 1, mb: 1, width: '100%', pt: 2 }}>
                  {['S', 'M', 'T', 'W', 'T', 'F', 'S'].map((day, idx) => (
                    <Box key={`loading-header-${idx}`} sx={{ p: 0.5, textAlign: 'center' }}>
                      <Skeleton variant="text" width="60%" sx={{ mx: 'auto' }} />
                    </Box>
                  ))}
                  {[...Array(35)].map((_, i) => (
                    <Box key={`loading-day-${i}`} sx={{ minHeight: 52, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                      <Skeleton variant="rounded" width={36} height={36} sx={{ borderRadius: '6px' }} animation="wave" />
                    </Box>
                  ))}
               </Box>
            ) : isMultiMonthRange ? (
              <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', minHeight: 350, gap: 2 }}>
                <Typography variant="h6" color="text.secondary">{t('reports.calendar.notAvailable')}</Typography>
                <Typography variant="body2" color="text.secondary" textAlign="center">
                  {t('reports.calendar.notAvailableMessage')}
                </Typography>
              </Box>
            ) : isTwoMonthRange ? (
              <Box sx={{ display: 'flex', gap: 2, overflowX: 'auto' }}>
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography variant="subtitle2" sx={{ mb: 1, color: 'text.secondary', textAlign: 'center', fontWeight: 600 }}>
                    {months[startDate.getMonth()]} {startDate.getFullYear()}
                  </Typography>
                  <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: 1 }}>
                    {WEEK_DAYS_SINGLE_LETTER.map((day, idx) => (
                      <Box key={`header-1-${idx}`} sx={{ p: 0.5, textAlign: 'center', fontWeight: 600, fontSize: '13px' }}>
                        {day}
                      </Box>
                    ))}
                    {(calendarData || []).map((dayData, index) => (
                      <Tooltip
                        key={`day-1-${index}`}
                        title={dayData.day ? `${dayData.tickets} ${t('reports.calendar.tickets')}` : ''}
                        open={activeDayTooltip === `day-1-${index}`}
                        onClose={closeDayTooltip}
                        {...dayTooltipProps}
                      >
                        <Box
                          onClick={() => handleDateClick(dayData)}
                          onMouseEnter={() => dayData.day && setActiveDayTooltip(`day-1-${index}`)}
                          onMouseLeave={closeDayTooltip}
                          sx={reportStyles.calendarDayBoxSmall(dayData, getTicketColor)}
                        >
                          <Box className="date-accent-bg">
                            {dayData.day}
                          </Box>
                        </Box>
                      </Tooltip>
                    ))}
                  </Box>
                </Box>
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography variant="subtitle2" sx={{ mb: 1, color: 'text.secondary', textAlign: 'center', fontWeight: 600 }}>
                    {months[(startDate.getMonth() + 1) % 12]} {startDate.getMonth() === 11 ? startDate.getFullYear() + 1 : startDate.getFullYear()}
                  </Typography>
                  <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: 1 }}>
                    {WEEK_DAYS_SINGLE_LETTER.map((day, idx) => (
                      <Box key={`header-2-${idx}`} sx={{ p: 0.5, textAlign: 'center', fontWeight: 600, fontSize: '13px' }}>
                        {day}
                      </Box>
                    ))}
                    {calendarData2?.map((dayData, index) => (
                      <Tooltip
                        key={`day-2-${index}`}
                        title={dayData.day ? `${dayData.tickets} ${t('reports.calendar.tickets')}` : ''}
                        open={activeDayTooltip === `day-2-${index}`}
                        onClose={closeDayTooltip}
                        {...dayTooltipProps}
                      >
                        <Box
                          onClick={() => handleDateClick(dayData)}
                          onMouseEnter={() => dayData.day && setActiveDayTooltip(`day-2-${index}`)}
                          onMouseLeave={closeDayTooltip}
                          sx={reportStyles.calendarDayBoxSmall(dayData, getTicketColor)}
                        >
                          <Box className="date-accent-bg">
                            {dayData.day}
                          </Box>
                        </Box>
                      </Tooltip>
                    ))}
                  </Box>
                </Box>
              </Box>
            ) : (
              <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: 1, mb: 1, width: '100%' }}>
                {WEEK_DAYS_SHORT.map((day, idx) => (
                  <Box key={`main-header-${idx}`} sx={{ p: 0.5, textAlign: 'center', fontWeight: 600, fontSize: '15px', color: COLORS.gray[600] }}>
                    {day.toUpperCase()}
                  </Box>
                ))}
                {(calendarData || []).map((dayData, index) => (
                  <Tooltip
                    key={`main-day-${index}`}
                    title={dayData.day ? `${dayData.tickets} ${t('reports.calendar.tickets')}` : ''}
                    open={activeDayTooltip === `main-day-${index}`}
                    onClose={closeDayTooltip}
                    {...dayTooltipProps}
                  >
                    <Box
                      onClick={() => handleDateClick(dayData)}
                      onMouseEnter={() => dayData.day && setActiveDayTooltip(`main-day-${index}`)}
                      onMouseLeave={closeDayTooltip}
                      sx={reportStyles.calendarDayBox(dayData, getTicketColor)}
                    >
                      <Box className="date-accent-bg">
                         {dayData.day}
                      </Box>
                    </Box>
                  </Tooltip>
                ))}
              </Box>
            )}

            {!isLoading && calendarData !== null && (
              <Box display="flex" alignItems="flex-end" gap={0.4} justifyContent="center" sx={{ mt: 1, mb: 1 }}>
                {[0, 1, 2, 3, 4, 5, 6].map((level) => (
                  <Box
                    key={level}
                    sx={{
                      width: 8,
                      height: 6 + (level * 2),
                      bgcolor: getTicketColor(level),
                      borderRadius: '1px',
                      transition: 'all 0.2s ease'
                    }}
                  />
                ))}
              </Box>
            )}
          </Box>
        </Collapse>
      </CardContent>
    </Card>
  );
}, (prevProps, nextProps) =>
  prevProps.selectedMonth === nextProps.selectedMonth &&
  prevProps.selectedYear === nextProps.selectedYear &&
  prevProps.calendarData === nextProps.calendarData &&
  prevProps.calendarData2 === nextProps.calendarData2 &&
  prevProps.isLoading === nextProps.isLoading &&
  prevProps.defaultExpanded === nextProps.defaultExpanded &&
  prevProps.canCollapse === nextProps.canCollapse
);

export default CalendarChart;
